import React from 'react';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import useTranslation from '../hooks/useTranslation';

const PageContainer = styled.div`
  max-width: 600px;
  margin: 0 auto;
  padding: 60px 20px;
  text-align: center;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const Icon = styled.div`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 30px;
  animation: scaleIn 0.5s ease-out;
  
  svg {
    width: 60px;
    height: 60px;
    color: white;
  }
  
  @keyframes scaleIn {
    from {
      transform: scale(0);
    }
    to {
      transform: scale(1);
    }
  }
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: bold;
  color: ${({ theme }) => theme.color.primaryText};
  margin-bottom: 15px;
`;

const Message = styled.p`
  font-size: 18px;
  color: ${({ theme }) => theme.color.secondaryText};
  margin-bottom: 30px;
  line-height: 1.6;
`;

const InfoCard = styled.div`
  background: ${({ theme }) => theme.color.cardBackground};
  border-radius: 16px;
  padding: 30px;
  margin: 30px 0;
  border: 1px solid rgba(245, 158, 11, 0.3);
`;

const InfoText = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.color.secondaryText};
  margin-bottom: 10px;
  line-height: 1.6;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 30px;
  flex-wrap: wrap;
  justify-content: center;
`;

const Button = styled.button`
  padding: 12px 30px;
  font-size: 16px;
  font-weight: bold;
  color: ${({ secondary }) => secondary ? ({ theme }) => theme.color.primaryText : '#fff'};
  background: ${({ secondary }) =>
    secondary ? 'transparent' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'};
  border: ${({ secondary }) => secondary ? '2px solid #667eea' : 'none'};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4);
  }
`;

const FeatureList = styled.div`
  text-align: left;
  margin: 20px 0;
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
  
  svg {
    width: 20px;
    height: 20px;
    color: #667eea;
    flex-shrink: 0;
  }
`;

const PaymentCancel = () => {
  const history = useHistory();
  const { t } = useTranslation();

  return (
    <PageContainer>
      <Icon>
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </Icon>

      <Title>❌ {t('payments.cancelTitle', 'Paiement annulé')}</Title>
      <Message>
        {t('payments.cancelMessage', "Vous avez annulé le processus de paiement. Aucun montant n'a été débité.")}
      </Message>

      <InfoCard>
        <InfoText style={{ fontWeight: 'bold', marginBottom: '15px' }}>
          💡 {t('payments.whyBuy', 'Pourquoi acheter des G-Coins ?')}
        </InfoText>

        <FeatureList>
          <FeatureItem>
            <svg fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <InfoText>{t('payments.featureUnlimitedStreams', 'Créez des streams et événements illimités')}</InfoText>
          </FeatureItem>

          <FeatureItem>
            <svg fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <InfoText>{t('payments.featureExclusiveTournaments', 'Participez à des tournois exclusifs')}</InfoText>
          </FeatureItem>

          <FeatureItem>
            <svg fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <InfoText>{t('payments.featurePremium', 'Accédez aux fonctionnalités premium')}</InfoText>
          </FeatureItem>

          <FeatureItem>
            <svg fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <InfoText>{t('payments.featureSecureStripe', 'Paiement 100% sécurisé par Stripe')}</InfoText>
          </FeatureItem>
        </FeatureList>
      </InfoCard>

      <Message style={{ fontSize: '14px' }}>
        🔒 {t('payments.securityNoticeLine1', 'Tous les paiements sont sécurisés et cryptés.')} <br />
        {t('payments.securityNoticeLine2', 'Vos informations bancaires ne sont jamais stockées sur nos serveurs.')}
      </Message>

      <ButtonGroup>
        <Button onClick={() => history.push('/gcoin-store')}>
          {t('payments.returnToStore', 'Retourner à la boutique')}
        </Button>
        <Button secondary onClick={() => history.push('/')}>
          {t('payments.backHome', "Retour à l'accueil")}
        </Button>
      </ButtonGroup>

      <Message style={{ fontSize: '12px', marginTop: '30px', opacity: 0.7 }}>
        {t('payments.needHelp', "Besoin d'aide ? Contactez notre support à support@corearena.tv")}
      </Message>
    </PageContainer>
  );
};

export default PaymentCancel;



